import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckIcon,
  CircleChevronDownIcon,
  CircleChevronUpIcon,
  CircleXIcon,
  GlobeIcon,
  InboxIcon,
  MailIcon,
  MinusIcon,
  PhoneIcon,
  SquareIcon,
  XIcon,
} from "lucide-react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import { DeviceVariantPickerModal } from "@/components/device-variant-picker-modal";
import { LoadingModal } from "@/components/modals/loading-modal";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupCardItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Timeline,
  TimelineContent,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
  TimelineSeparator,
  TimelineTitle,
} from "@/components/ui/timeline";
import { cn, formatCurrency, generateBookedID } from "@/lib/utils";
import {
  useBookDeviceMutation,
  useBookMSISDNMutation,
  useGetPlanOfferingsQuery,
  useLazyGetAvailableMSISDNsQuery,
} from "@/redux/services/order-portal";
import { setOrder } from "@/redux/slices/order";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { selectPlanSchema } from "@/shared/schemas/select-plan";

export const SelectPlanForm = ({
  serviceType,
  className,
}: {
  serviceType: ServiceType;
  className?: string;
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Necessary
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // const isProduction = import.meta.env.VITE_ENV === "production";

  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const { data: planOfferings, isLoading: isLoadingPlanOfferings } =
    useGetPlanOfferingsQuery({
      order_type: order.hasExistingServiceProvider ? "Port-IN" : "New Order",
      // customer_type should be non-null at this order step
      customer_type: order.customerType as CustomerType,
      network:
        serviceType === "mobi-5g" || serviceType === "freedom-5g"
          ? "5G"
          : undefined,
      ic_number: order.nricNumber !== "" ? order.nricNumber : undefined,
      internal_portin: !!(
        order.existingServiceProvider?.name === "Infinity" ||
        order.existingServiceProvider?.name === "EASI"
      ),
      sales_channel: 7,
    });
  const [bookDevice] = useBookDeviceMutation();
  const [getAvailableMSISDNs] = useLazyGetAvailableMSISDNsQuery();
  const [bookMSISDN] = useBookMSISDNMutation();

  const form = useForm<z.infer<typeof selectPlanSchema>>({
    resolver: zodResolver(selectPlanSchema),
    defaultValues: {
      // customer_type should be non-null at this order step
      customerType: order.customerType as CustomerType,
      plan: {
        id:
          order.plan.id && order.plan.id !== 0 ? order.plan.id.toString() : "",
        name: order.plan.name ?? "",
        price: order.plan.price ?? "",
      },
      automaticNumberSelection: order.automaticNumberSelection ?? false,
      sim: order.sim
        ? {
            id: order.sim.id.toString() ?? "",
            name: order.sim.name ?? "",
            price: order.sim.price ?? "",
          }
        : undefined,
      device: order.device
        ? {
            id: order.device.id.toString() ?? "",
            name: order.device.name ?? "",
            price: order.device.price ?? "",
          }
        : undefined,
      addon: order.addon
        ? {
            id: order.addon.id.toString() ?? "",
            name: order.addon.name ?? "",
            price: order.addon.price ?? "",
          }
        : undefined,
    },
  });

  const selectedPlanId = useWatch({ control: form.control, name: "plan.id" });
  const selectedPlanName = useWatch({
    control: form.control,
    name: "plan.name",
  });
  const selectedPlanPrice = useWatch({
    control: form.control,
    name: "plan.price",
  });
  const selectedSimId = useWatch({ control: form.control, name: "sim.id" });
  const selectedSimName = useWatch({ control: form.control, name: "sim.name" });
  const selectedSimPrice = useWatch({
    control: form.control,
    name: "sim.price",
  });
  const selectedDeviceId = useWatch({
    control: form.control,
    name: "device.id",
  });
  const selectedDeviceName = useWatch({
    control: form.control,
    name: "device.name",
  });
  const selectedDevicePrice = useWatch({
    control: form.control,
    name: "device.price",
  });
  const selectedAddonId = useWatch({ control: form.control, name: "addon.id" });
  const selectedAddonName = useWatch({
    control: form.control,
    name: "addon.name",
  });
  const selectedAddonPrice = useWatch({
    control: form.control,
    name: "addon.price",
  });

  function getTimelineTitle() {
    if (serviceType === "infinity") {
      return t("plans.titles.infinity");
    }
    if (serviceType === "freedom" || serviceType === "freedom-5g") {
      return t("plans.titles.freedom");
    }
    if (serviceType === "mobi" || serviceType === "mobi-5g") {
      return t("plans.titles.mobi");
    }

    return t("plans.titles.sim-card");
  }

  function getSelectedPlan(planId?: string) {
    if (planId || selectedPlanId) {
      for (const planOffering of planOfferings ?? []) {
        const plan = planOffering.member.find(
          (member) =>
            member.bundle_id &&
            member.bundle_id.toString() === (planId ?? selectedPlanId)
        );

        if (plan) {
          return plan;
        }
      }
    }

    return;
  }

  function getVoicePrice() {
    const selectedPlan = getSelectedPlan();

    if (selectedPlan?.relation && selectedPlan.relation.length > 0) {
      const voicePrice = selectedPlan.relation.find(
        (relation) => relation.name === "Voice"
      )?.price_amount;

      if (voicePrice) {
        return Number(voicePrice);
      }
    }

    return;
  }
  const voicePrice = getVoicePrice();

  function getTotalPrice() {
    const selectedPlan = getSelectedPlan();

    const planPriceString = selectedPlanPrice
      ? selectedPlanPrice.substring(1)
      : "0.00";
    const planPrice = Number(planPriceString);
    const simPrice = selectedSimPrice ?? 0;
    const devicePrice = selectedDevicePrice ?? 0;
    const addonPrice = selectedAddonPrice ?? 0;
    const miscPrice =
      selectedPlan?.relation && selectedPlan.relation.length > 0
        ? selectedPlan.relation.reduce(
            (acc, relation) => acc + Number(relation.price_amount),
            0
          )
        : 0;

    return planPrice + simPrice + devicePrice + addonPrice + miscPrice;
  }

  const onSubmit: SubmitHandler<z.infer<typeof selectPlanSchema>> = async (
    data
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Necessary
  ) => {
    try {
      const selectedPlan = getSelectedPlan();
      let msisdnNumber = order.msisdnNumber ?? "";
      let bookedId = order.bookedId ?? "";

      const bundles: number[] = [
        Number(data.plan.id),
        ...(data.sim?.id && data.sim.id !== "" ? [Number(data.sim.id)] : []),
        ...(data.device?.id && data.device.id !== ""
          ? [Number(data.device.id)]
          : []),
        ...(data.addon?.id && data.addon.id !== ""
          ? [Number(data.addon.id)]
          : []),
      ];

      if (data.device?.id && data.device.id !== "") {
        bookedId = generateBookedID();

        const bookDeviceResponse = await bookDevice({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          booked_id: bookedId,
          delivery_type: 2,
          store_id: 10,
          warehouse_id: 5,
          check_stock: true,
          order_details:
            data.device.name && data.device.sku
              ? [
                  {
                    name: data.device.name,
                    sku_code: data.device.sku,
                  },
                ]
              : [],
        });
        if (bookDeviceResponse.error) {
          throw new Error("Failed to book device");
        }
      }

      if (
        serviceType !== "infinity" &&
        serviceType !== "tourist" &&
        !order.hasExistingServiceProvider &&
        data.automaticNumberSelection &&
        !order.msisdnNumber
      ) {
        const getAvailableMSISDNsResponse = await getAvailableMSISDNs({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          category: "Recycled",
          sim_no: "",
        });
        if (getAvailableMSISDNsResponse.error) {
          throw new Error("Failed to get available service numbers");
        }
        if (getAvailableMSISDNsResponse.data?.length === 0) {
          throw new Error("No available service numbers");
        }

        // if (isProduction) {
        const bookMSISDNResponse = await bookMSISDN({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          // biome-ignore lint/style/noNonNullAssertion: Already checked that array is not empty
          msisdnID: getAvailableMSISDNsResponse.data![0].msisdnId,
          // biome-ignore lint/style/noNonNullAssertion: Already checked that array is not empty
          msisdnNo: getAvailableMSISDNsResponse.data![0].msisdnNo,
        });
        if (bookMSISDNResponse.error || !bookMSISDNResponse.data) {
          throw new Error("Failed to book service number");
        }
        // }

        // biome-ignore lint/style/noNonNullAssertion: Already checked that array is not empty
        msisdnNumber = getAvailableMSISDNsResponse.data![0].msisdnNo;
      }

      const updatedOrder = {
        ...order,
        plan: {
          id: Number(data.plan.id),
          name: data.plan.name,
          price: data.plan.price,
        },
        contractId: selectedPlan?.contract_id,
        automaticNumberSelection: data.automaticNumberSelection,
        sim:
          data.sim?.id && data.sim.id !== ""
            ? {
                id: Number(data.sim.id),
                name: data.sim.name,
                price: Number(data.sim.price),
              }
            : undefined,
        device:
          data.device?.id && data.device.id !== ""
            ? {
                id: Number(data.device.id),
                sku: data.device.sku,
                name: data.device.name,
                color: data.device.color,
                price: Number(data.device.price),
              }
            : undefined,
        bookedId: bookedId ?? undefined,
        addon:
          data.addon?.id && data.addon.id !== ""
            ? {
                id: Number(data.addon.id),
                name: data.addon.name,
                price: Number(data.addon.price),
              }
            : undefined,
        bundles,
        msisdnId: order.msisdnId,
        msisdnNumber,
        imsiNumber: data.automaticNumberSelection
          ? undefined
          : order.imsiNumber,
      };

      dispatch(setOrder(updatedOrder));

      form.reset();

      if (
        serviceType !== "infinity" &&
        serviceType !== "tourist" &&
        !order.hasExistingServiceProvider &&
        !data.automaticNumberSelection
      ) {
        navigate(`/${serviceType}/select-number`);
      } else {
        navigate(`/${serviceType}/customer-information`);
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <>
      {isLoadingPlanOfferings && (
        <LoadingModal isOpen={isLoadingPlanOfferings} />
      )}

      <Form {...form}>
        <form
          className={cn("flex flex-col gap-6 sm:flex-row", className)}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="sm:basis-2/3">
            <Timeline defaultValue={5}>
              <TimelineItem step={1}>
                <TimelineHeader>
                  <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                  <TimelineTitle className="-mt-1">
                    {getTimelineTitle()}
                  </TimelineTitle>
                  <TimelineIndicator className="flex size-6 items-center justify-center">
                    <MinusIcon className="size-4" />
                  </TimelineIndicator>
                </TimelineHeader>
                <TimelineContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="plan.id"
                    render={({ field }) => (
                      <FormItem className="gap-4">
                        <FormLabel className="sr-only">
                          {t("labels.plan")}
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={(value) => {
                              field.onChange(value);

                              const selectedPlan = getSelectedPlan(value);
                              form.setValue(
                                "plan.name",
                                selectedPlan?.name ?? ""
                              );
                              form.setValue(
                                "plan.price",
                                selectedPlan?.price ?? ""
                              );

                              form.setValue("sim.id", "");
                              form.setValue("sim.name", "");
                              form.setValue("sim.price", 0);
                              form.setValue("device", undefined);
                              form.setValue("addon", undefined);
                            }}
                            value={field.value}
                          >
                            <Accordion
                              className="space-y-2"
                              collapsible
                              type="single"
                            >
                              {planOfferings?.map((planOffering) => (
                                <AccordionItem
                                  key={planOffering.name}
                                  value={planOffering.name}
                                >
                                  <AccordionTrigger className="uppercase">
                                    {planOffering.name}
                                  </AccordionTrigger>
                                  <AccordionContent className="mt-4 ml-4 space-y-4">
                                    {planOffering.member.map((member) => {
                                      if (!member.bundle_id) {
                                        return null;
                                      }

                                      return (
                                        <div
                                          className="relative"
                                          key={member.bundle_id}
                                        >
                                          <RadioGroupCardItem
                                            className="peer w-full space-y-2"
                                            id={member.bundle_id.toString()}
                                            value={member.bundle_id.toString()}
                                            variant="outline"
                                          >
                                            <div className="flex items-center justify-between gap-4 font-semibold text-primary">
                                              <p className="text-left">
                                                {member.name}
                                              </p>
                                              <p className="text-right">
                                                {member.price}
                                              </p>
                                            </div>
                                            <Separator />
                                            {member.spec && (
                                              <div className="flex flex-wrap items-center gap-4">
                                                {planOffering.customer_type ===
                                                  "RES" && (
                                                  <>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <CircleChevronDownIcon />
                                                      <p>
                                                        {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                        {member.spec.download}
                                                      </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <CircleChevronUpIcon />
                                                      <p>
                                                        {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                        {member.spec.upload}
                                                      </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <GlobeIcon />
                                                      {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                      <p>{member.spec.quota}</p>
                                                    </div>
                                                  </>
                                                )}
                                                {planOffering.customer_type ===
                                                  "MOBI-POSTPAID" && (
                                                  <>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <PhoneIcon />
                                                      {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                      <p>{member.spec.call}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <MailIcon />
                                                      {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                      <p>{member.spec.text}</p>
                                                    </div>
                                                    <div className="flex items-center gap-2 [&_svg]:size-4">
                                                      <GlobeIcon />
                                                      {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                      <p>{member.spec.data}</p>
                                                    </div>
                                                  </>
                                                )}
                                                {planOffering.customer_type ===
                                                  "FREEDOM" && (
                                                  <div className="flex items-center gap-2 [&_svg]:size-4">
                                                    <GlobeIcon />
                                                    {/* @ts-expect-error Typescript cannot narrow down the type of spec */}
                                                    <p>{member.spec.data}</p>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </RadioGroupCardItem>
                                          <div className="absolute -top-2.5 -left-2.5 hidden border-2 border-primary bg-background p-0.25 peer-data-[state=checked]:block">
                                            <SquareIcon className="size-4 fill-primary stroke-none" />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {serviceType !== "infinity" &&
                    serviceType !== "tourist" &&
                    !order.hasExistingServiceProvider && (
                      <FormField
                        control={form.control}
                        name="automaticNumberSelection"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("labels.automaticNumberSelection")}
                            </FormLabel>
                            <FormControl>
                              <div className="relative inline-grid h-6 w-12 grid-cols-[1fr_1fr] items-center font-medium text-sm">
                                <Switch
                                  checked={field.value}
                                  className="peer absolute inset-0 h-[inherit] w-auto data-[state=unchecked]:bg-input/50 [&_span]:z-10 [&_span]:h-full [&_span]:w-1/2 [&_span]:transition-transform [&_span]:duration-300 [&_span]:ease-[cubic-bezier(0.16,1,0.3,1)] [&_span]:data-[state=checked]:translate-x-full [&_span]:data-[state=checked]:rtl:-translate-x-full"
                                  onCheckedChange={field.onChange}
                                />
                                <span className="pointer-events-none relative ms-1 flex items-center justify-center text-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] peer-data-[state=checked]:invisible peer-data-[state=unchecked]:translate-x-full peer-data-[state=unchecked]:rtl:-translate-x-full">
                                  <XIcon className="size-4" />
                                </span>
                                <span className="pointer-events-none relative me-1 flex items-center justify-center text-center transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] peer-data-[state=unchecked]:invisible peer-data-[state=checked]:-translate-x-full peer-data-[state=checked]:text-background peer-data-[state=checked]:rtl:translate-x-full">
                                  <CheckIcon className="size-4" />
                                </span>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                </TimelineContent>
              </TimelineItem>
              {serviceType !== "infinity" && (
                <TimelineItem step={2}>
                  <TimelineHeader>
                    <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                    <TimelineTitle className="-mt-1">
                      {t("plans.titles.sim-option")}
                    </TimelineTitle>
                    <TimelineIndicator className="flex size-6 items-center justify-center">
                      <MinusIcon className="size-4" />
                    </TimelineIndicator>
                  </TimelineHeader>
                  <TimelineContent>
                    <FormField
                      control={form.control}
                      name="sim.id"
                      render={({ field }) => (
                        <FormItem className="gap-4">
                          <FormLabel className="sr-only">
                            {t("labels.sim")}
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              className="mt-4 ml-4 grid grid-cols-1 gap-6 sm:grid-cols-3"
                              key={selectedPlanId}
                              onValueChange={(value) => {
                                field.onChange(value);

                                const selectedPlan = getSelectedPlan();
                                const selectedSim =
                                  selectedPlan?.sim_option?.find(
                                    (sim) => sim.bundle_id?.toString() === value
                                  );

                                form.setValue(
                                  "sim.name",
                                  selectedSim?.item_name ?? ""
                                );
                                form.setValue(
                                  "sim.price",
                                  selectedSim?.price_amount ?? 0
                                );
                              }}
                              value={field.value}
                            >
                              {getSelectedPlan()?.sim_option?.map(
                                (simOption) => {
                                  if (!simOption.bundle_id) {
                                    return null;
                                  }

                                  return (
                                    <div
                                      className="relative basis-1/3"
                                      key={simOption.bundle_id}
                                    >
                                      <RadioGroupCardItem
                                        className="peer size-full space-y-2 text-left"
                                        id={simOption.bundle_id.toString()}
                                        value={simOption.bundle_id.toString()}
                                        variant="outline"
                                      >
                                        <p className="font-semibold text-base text-primary">
                                          {simOption.item_name}
                                        </p>
                                        <p className="text-muted-foreground">
                                          {formatCurrency(
                                            simOption.price_amount
                                          )}
                                        </p>
                                      </RadioGroupCardItem>
                                      <div className="absolute -top-2.5 -left-2.5 hidden border-2 border-primary bg-background p-0.25 peer-data-[state=checked]:block">
                                        <SquareIcon className="size-4 fill-primary stroke-none" />
                                      </div>
                                    </div>
                                  );
                                }
                              )}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TimelineContent>
                </TimelineItem>
              )}
              {serviceType !== "tourist" && (
                <TimelineItem step={3}>
                  <TimelineHeader>
                    <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                    <TimelineTitle className="-mt-1">
                      {t("plans.titles.device-option")}
                    </TimelineTitle>
                    <TimelineIndicator className="flex size-6 items-center justify-center">
                      <MinusIcon className="size-4" />
                    </TimelineIndicator>
                  </TimelineHeader>
                  <TimelineContent className="space-y-6">
                    <Accordion className="space-y-2" collapsible type="single">
                      {Object.entries(getSelectedPlan()?.devices || {}).map(
                        ([category, devices]) => {
                          if (category === "ADDON") {
                            return null;
                          }

                          return (
                            <AccordionItem key={category} value={category}>
                              <AccordionTrigger className="uppercase">
                                {category}
                              </AccordionTrigger>
                              <AccordionContent className="mt-4 ml-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                                {Object.entries(devices).map(
                                  ([deviceName, variants]) => {
                                    const isSelected = variants.find(
                                      (variant) =>
                                        variant.bundle_id &&
                                        variant.bundle_id.toString() ===
                                          selectedDeviceId
                                    );

                                    return (
                                      <div
                                        className="relative"
                                        key={deviceName}
                                      >
                                        <DeviceVariantPickerModal
                                          className={
                                            isSelected && "border-primary"
                                          }
                                          // customer_type should be non-null at this order step
                                          customerType={
                                            order.customerType as CustomerType
                                          }
                                          deviceName={deviceName}
                                          onChange={(value) => {
                                            form.setValue("device", value);
                                          }}
                                          variants={variants}
                                        />
                                        <div
                                          className={cn(
                                            "absolute -top-2.5 -left-2.5 border-2 border-primary bg-background p-0.25",
                                            isSelected ? "block" : "hidden"
                                          )}
                                        >
                                          <SquareIcon className="size-4 fill-primary stroke-none" />
                                        </div>
                                      </div>
                                    );
                                  }
                                )}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        }
                      )}
                    </Accordion>
                  </TimelineContent>
                </TimelineItem>
              )}
              {serviceType !== "easi" && serviceType !== "tourist" && (
                <TimelineItem step={4}>
                  <TimelineHeader>
                    <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                    <TimelineTitle className="-mt-1">
                      {t("plans.titles.addon-option")}
                    </TimelineTitle>
                    <TimelineIndicator className="flex size-6 items-center justify-center">
                      <MinusIcon className="size-4" />
                    </TimelineIndicator>
                  </TimelineHeader>
                  <TimelineContent>
                    <FormField
                      control={form.control}
                      name="addon.id"
                      render={({ field }) => (
                        <FormItem className="gap-4">
                          <FormLabel className="sr-only">
                            {t("labels.addon")}
                          </FormLabel>
                          <FormControl>
                            <RadioGroup
                              onValueChange={(value) => {
                                field.onChange(value);

                                const selectedPlan = getSelectedPlan();
                                form.setValue(
                                  "addon.name",
                                  selectedPlan?.devices?.ADDON?.ADDON?.find(
                                    (addon) => addon.bundle_id === Number(value)
                                  )?.bundle_name ?? ""
                                );
                                form.setValue(
                                  "addon.price",
                                  selectedPlan?.devices?.ADDON?.ADDON?.find(
                                    (addon) => addon.bundle_id === Number(value)
                                  )?.price_amount ?? 0
                                );
                              }}
                              value={field.value}
                            >
                              <Accordion
                                className="space-y-2"
                                collapsible
                                type="single"
                              >
                                {Object.entries(
                                  getSelectedPlan()?.devices || {}
                                ).map(([category, addons]) => {
                                  if (category !== "ADDON") {
                                    return null;
                                  }

                                  return (
                                    <AccordionItem
                                      key={category}
                                      value={category}
                                    >
                                      <AccordionTrigger className="uppercase">
                                        {category}
                                      </AccordionTrigger>
                                      <AccordionContent className="mt-4 ml-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
                                        {addons.ADDON.map((addon) => {
                                          if (!addon.bundle_id) {
                                            return null;
                                          }

                                          return (
                                            <div
                                              className="relative"
                                              key={addon.bundle_id}
                                            >
                                              <RadioGroupCardItem
                                                className="peer w-full space-y-2 text-left"
                                                id={addon.bundle_id.toString()}
                                                key={addon.bundle_id}
                                                value={addon.bundle_id.toString()}
                                                variant="outline"
                                              >
                                                <p className="font-semibold text-primary">
                                                  {addon.bundle_name}
                                                </p>
                                                <p className="text-muted-foreground">
                                                  {formatCurrency(
                                                    addon.price_amount
                                                  )}
                                                </p>
                                              </RadioGroupCardItem>
                                              <div className="absolute -top-2.5 -left-2.5 hidden border-2 border-primary bg-background p-0.25 peer-data-[state=checked]:block">
                                                <SquareIcon className="size-4 fill-primary stroke-none" />
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </AccordionContent>
                                    </AccordionItem>
                                  );
                                })}
                              </Accordion>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </TimelineContent>
                </TimelineItem>
              )}
              {serviceType === "infinity" && (
                <TimelineItem step={5}>
                  <TimelineHeader>
                    <TimelineSeparator className="group-data-[orientation=vertical]/timeline:h-[calc(100%-1.5rem-0.25rem)] group-data-[orientation=vertical]/timeline:translate-y-6.5" />
                    <TimelineTitle className="-mt-1">
                      {t("plans.titles.promo")}
                    </TimelineTitle>
                    <TimelineIndicator className="flex size-6 items-center justify-center">
                      <MinusIcon className="size-4" />
                    </TimelineIndicator>
                  </TimelineHeader>
                  <TimelineContent />
                </TimelineItem>
              )}
            </Timeline>
          </div>

          <div className="sm:basis-1/3">
            <Card>
              <CardHeader>
                <CardTitle>{t("plan-summary.titles.summary")}</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPlanId ? (
                  <div className="space-y-6 text-sm">
                    <div className="space-y-1">
                      <p className="text-muted-foreground uppercase">
                        {serviceType === "infinity"
                          ? t("plan-summary.titles.internet")
                          : t("plan-summary.titles.sim-card")}
                      </p>
                      <Separator />
                      <div>
                        <div className="flex items-center justify-between gap-4">
                          <p>{selectedPlanName} (x1)</p>
                          <p className="text-base text-primary">
                            {selectedPlanPrice}
                          </p>
                        </div>
                      </div>
                    </div>
                    {serviceType === "infinity" && voicePrice && (
                      <div className="space-y-1">
                        <p className="text-muted-foreground uppercase">
                          {t("plan-summary.titles.voice")}
                        </p>
                        <Separator />
                        <div className="flex items-center justify-between gap-4">
                          <p>{t("labels.voice")}</p>
                          <p className="text-base text-primary">
                            {formatCurrency(voicePrice)}
                          </p>
                        </div>
                      </div>
                    )}
                    {serviceType !== "infinity" && selectedSimId && (
                      <div className="space-y-1">
                        <p className="text-muted-foreground uppercase">
                          {t("plan-summary.titles.sim-option")}
                        </p>
                        <Separator />
                        <div className="flex items-center justify-between gap-4">
                          <p>{selectedSimName}</p>
                          <p className="text-base text-primary">
                            {formatCurrency(selectedSimPrice ?? 0)}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedDeviceId && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-muted-foreground uppercase">
                            {t("plan-summary.titles.device")}
                          </p>
                          <Button
                            className="size-6 text-destructive"
                            onClick={() => {
                              form.setValue("device.id", "");
                              form.setValue("device.sku", "");
                              form.setValue("device.name", "");
                              form.setValue("device.color", "");
                              form.setValue("device.price", 0);
                            }}
                            size="icon"
                            type="button"
                            variant="ghost"
                          >
                            <CircleXIcon />
                            <span className="sr-only">
                              {t("labels.remove-device")}
                            </span>
                          </Button>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between gap-4">
                          <p>{selectedDeviceName}</p>
                          <p className="text-base text-primary">
                            {formatCurrency(selectedDevicePrice ?? 0)}
                          </p>
                        </div>
                      </div>
                    )}
                    {selectedAddonId && (
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-4">
                          <p className="text-muted-foreground uppercase">
                            {t("plan-summary.titles.addon")}
                          </p>
                          <Button
                            className="size-6 text-destructive"
                            onClick={() => {
                              form.setValue("addon.id", "");
                              form.setValue("addon.name", "");
                              form.setValue("addon.price", 0);
                            }}
                            size="icon"
                            type="button"
                            variant="ghost"
                          >
                            <CircleXIcon />
                            <span className="sr-only">
                              {t("labels.remove-addon")}
                            </span>
                          </Button>
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between gap-4">
                          <p>{selectedAddonName}</p>
                          <p className="text-base text-primary">
                            {formatCurrency(selectedAddonPrice ?? 0)}
                          </p>
                        </div>
                      </div>
                    )}
                    {getSelectedPlan()?.relation?.length && (
                      <div className="space-y-1">
                        <p className="text-muted-foreground uppercase">
                          {t("plan-summary.titles.other")}
                        </p>
                        <Separator />
                        {getSelectedPlan()?.relation?.map((relation) => {
                          if (relation.name === "Voice") {
                            return null;
                          }

                          return (
                            <div
                              className="flex items-center justify-between gap-4"
                              key={relation.id}
                            >
                              <p>{relation.name}</p>
                              <p className="text-base text-primary">
                                {formatCurrency(Number(relation.price_amount))}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex h-24 flex-col items-center justify-center gap-2 text-muted-foreground">
                    <InboxIcon className="size-10" />
                    <p>{t("labels.no-data")}</p>
                  </div>
                )}
              </CardContent>
              <Separator className="my-2" />
              <CardFooter className="-mt-4 flex-col space-y-6 *:w-full">
                <div className="flex justify-between">
                  <p>{t("labels.total")}</p>
                  <p className="text-primary">
                    {formatCurrency(getTotalPrice())}
                  </p>
                </div>

                <SubmitButton
                  className="rounded-none"
                  isSubmitting={form.formState.isSubmitting}
                >
                  {t("labels.next")}
                </SubmitButton>
              </CardFooter>
            </Card>
          </div>
        </form>
      </Form>
    </>
  );
};
